import React, { useState, useEffect } from 'react';

import { useOutletContext, useNavigate } from "react-router-dom";
import { useBreadStorageContext } from "contexts/BreadStorageContext";
import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from "lodash";
import { createColumnHelper } from "@tanstack/react-table";

import { Grid, Box, Typography, } from "@mui/material";

import TableField from "components/Table";
import SearchInput from "components/SearchInput";
import ModalContainer from 'components/ModalContainer';
import { SolidButton } from "components/buttons/SolidButton";

import ModalforCheck from '../modalCheck.js';
import ModalForAddOrgOrSite from '../ModalForAddOrgOrSite.js';

import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";

import { mergeOrgData } from "utils/getHierarchy";

const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const Site = () => {
  const {scopeData, setScopeData, scopeReady} = useOutletContext();

  const { bread, setBread } = useBreadStorageContext();

  const [orgSiteUuid, _setOrgSiteUuid] = useState(uuidv4());
  const [nowUuid, setNowUuid] = useState('');
  
  const { dataMap, sendRequest } = useDataSourceManager();
  
  const [openModalForSite, setOpenModalForSite] = useState(false);
  const [modalForSiteTitle, setModalForSiteTitle] = useState('');
  const [modalForSiteCancelBtn, setModalForSiteCancelBtn] = useState('');
  const [modalForSiteSaveBtn, setModalForSiteSaveBtn] = useState('');
  
  const [openModalCheck, setOpenModalCheck] = useState(false);
  const [modalCheckTitle, setModalCheckTitle] = useState('');
  const [modalCheckContent, setModalCheckContent] = useState('');
  const [modalCheckSaveBtn, setModalCheckSaveBtn] = useState('');
  const [modalCheckCancelBtn, setModalCheckCancelBtn] = useState('');
  
  const [modalFrom, setModalFrom] = useState('');
  const [modalAction, setModalAction] = useState(false);
  const [updateItem, setUpdateItem] = useState({});
  const [enableAction, setEnableAction] = useState(false);

  const getDatas = async () => {
    setNowUuid(orgSiteUuid)

    let tmpSelectedBread = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');
    let tmpResource = '';
    let tmpResourceAPIval = {}

    if (Object.keys(tmpSelectedBread.orgs).length === 0) {
      tmpResource = '/orgs/:orgId/sites.read';
      tmpResourceAPIval = { orgId: tmpSelectedBread.realm.id }
    } else {
      tmpResource = '/ents/:entId/orgs/:orgId/sites.read';
      tmpResourceAPIval = { 
        entId: tmpSelectedBread.realm.id,
        orgId: tmpSelectedBread.orgs.id
      }
    }
    sendRequest( tmpResource, orgSiteUuid, tmpResourceAPIval);
  };

  const handleAddItem = () =>{
    if(!enableAction) return;
    setModalFrom('Add')
    let newItem = {
      id:'',name: '', hasManageOrg: false, 
      roleId: '',roleName: '',
    }
    setUpdateItem(newItem)
    
    setModalForSiteTitle('Add New Site')
    setModalForSiteCancelBtn('Cancel')
    setModalForSiteSaveBtn('Save')
    setOpenModalForSite(true)
  }

  const handleScopeLink = (item) =>{
    let tmpSelectedBread = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');
    
    // save storage
    tmpSelectedBread.site = item;
    localStorage.setItem('selectBreadcrumbs', JSON.stringify(tmpSelectedBread));
    setBread(tmpSelectedBread)
    // navigate
    navigatePage(item);
  }

  const navigate = useNavigate();
  const navigatePage = () => {
    let path = "/site/dashboard";
    navigate(path);
  }

  const checkPermissions = () => {
    let tmpSelectedBread = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');
    if(tmpSelectedBread && Object.keys(tmpSelectedBread).length > 0){
      if(Object.keys(tmpSelectedBread.orgs).length === 0){
        if(tmpSelectedBread.realm.roleId === 5 || tmpSelectedBread.realm.roleId === 6){
          // stand alone 放在 realm, but role 走 orgs （5 & 6)
          setEnableAction(true)
        }else{
          setEnableAction(false)
        }
      }else{
        if(tmpSelectedBread.orgs.roleId === 5 || tmpSelectedBread.orgs.roleId === 6){
          setEnableAction(true)
        }else{
          setEnableAction(false)
        }
      } 
    }
  }

  useEffect(() => {
    checkPermissions()
  }, [bread]);

  useEffect(() => {
    if(scopeReady){
      checkPermissions()
    }
  }, [scopeReady]);

  function handleActionDenied(message) {
    setOpenModalForSite(false);
    setModalCheckTitle('Action denied')
    setModalCheckContent(message)
    setModalCheckCancelBtn('')
    setModalCheckSaveBtn('OK')
    setModalFrom('error')
    setOpenModalCheck(true)
  }

  useEffect(() => {
    if(nowUuid !== ''){
      switch (nowUuid) {
        case orgSiteUuid:
          if(dataMap[orgSiteUuid]?.code !== undefined){
            setModalCheckTitle('Action denied')
            setModalCheckContent(dataMap[orgSiteUuid].message)
            setModalCheckCancelBtn('')
            setModalCheckSaveBtn('OK')
            setModalFrom('error')
            setOpenModalCheck(true)
          }else if(dataMap[orgSiteUuid] !== undefined){
            const result = mergeOrgData(dataMap[orgSiteUuid])
            setScopeData(prev => ({
              ...prev,
              site: result
            }));
          }

          setNowUuid('')
          break;
        default:
          break;
      }
    }
  }, [dataMap]);

  useEffect(() => {
    if(modalAction === true){
      if(modalFrom === 'Add'){
        if(checkMockup === 'true'){
          let addItem = cloneDeep(updateItem)
          addItem.id = uuidv4();

          setScopeData(prev => ({
            ...prev,
            site: [...prev.site, addItem]
          }));
        }
      }
      setModalAction(false)
    }
  }, [modalAction]);

  const columnHelper = createColumnHelper();
  const columnsData = [
    columnHelper.accessor("name", {
      header: "Name",
      cell: (info) => (
        <div onClick={() => handleScopeLink(info.row.original)} style={{color:'#707ca8', cursor:'pointer', }}>
          <span style={{whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', textDecoration:'underline',}}>
            {info.getValue()}
            </span>
        </div>
      ),
    }),
    columnHelper.accessor("roleName", {
      header: "Role",
    }),
  ];

  const table = useTableInstance({
    tableName: "hierarchyRealmSite",
    data: scopeData.site,
    columns: columnsData,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    enableRowSelection: true,
  });

  const emptyHead = useTableInstance({
    tableName: "emptyRealmSite",
    data: '',
    columns: columnsData,
    enableSorting: true,
  });

  return (
      <>
        <div className={`main-card-content`}
          style={{flex: (scopeData.site.length>0)?'':'1', 
          display:'flex', flexDirection:'column'}}>
          <div>
            <Grid container style={{justifyContent:'space-between', marginBottom:'10px'}}>
              <Box style={{display:'flex'}}>
                <Grid container sx={{alignItems: "center",}}>
                </Grid>
              </Box>
              <Box style={{display:'flex'}}>
                { scopeData.site.length>0 && <Grid container sx={{alignItems: "center", gap:'15px'}}>
                  <SearchInput
                    value={table.globalFilter}
                    onChange={table.setGlobalFilter}
                  />
                  {enableAction && 
                    <SolidButton 
                      disabled={enableAction? false: true}
                      startIcon={<AddIcon />}
                      height="32px"
                      onClick={() => handleAddItem()}
                    >
                      Add Site
                    </SolidButton>
                  }
                </Grid> }
              </Box>
            </Grid>
          </div>
          {scopeData.site.length > 0 && 
            <TableField
            {...table}
            />
          }
          { scopeData.site.length === 0 && <>
            <TableField
              {...emptyHead}
            />    
            <div style={{flex:'1',alignContent:'center', justifyItems:'center'}}>
              <Grid container style={{justifyContent:'center', marginBottom:'40px'}}>
                <Typography sx={{textAlign:'center'}}
                fontSize="16px" fontWeight="600" color="rgba(0, 0, 0, 0.8)">
                  No entry in the table, you can create a new site.
                </Typography>
              </Grid>
              <Box>
                {enableAction && 
                  <SolidButton 
                    disabled={enableAction? false: true}
                    startIcon={<AddIcon />}
                    height="32px"
                    onClick={() => handleAddItem()}
                  >
                    Add Site
                  </SolidButton>
                }
              </Box>
            </div> 
          </>}
        </div>
        <ModalContainer
          display={openModalCheck}
          modalWidth={'500px'} //550px, 750px, empty=1000px,
          children={
            <ModalforCheck
              title={modalCheckTitle}
              content={modalCheckContent}
              cancelBtn={modalCheckCancelBtn}
              saveBtn={modalCheckSaveBtn}
              setOpenModal={setOpenModalCheck}
              setModalAction={setModalAction}
            />
          }
        />
        <ModalForAddOrgOrSite
          auth="Site"
          display={openModalForSite}
          title={modalForSiteTitle}
          setOpenModal={setOpenModalForSite}
          cancelBtn={modalForSiteCancelBtn}
          saveBtn={modalForSiteSaveBtn}
          errorHandling={handleActionDenied}
          refresh={getDatas}
        />
      </>
  );
};

export default Site;